<script setup lang="ts">
  import { ref, computed, onMounted, watch, nextTick } from 'vue'
  import { useUser } from '@/composables/useUser'
  import RevoGrid, {
    CellProps,
    ColumnGrouping,
    ColumnProp,
    ColumnRegular,
    DataType
  } from '@revolist/vue3-datagrid'
  import Spinner from '@/shared/components/Spinner.vue'
  import { GetFileDataResponse } from './FileManager/models/GetFileDataResponse'
  import {
    AplosFileApiService,
    IAplosFileApiService
  } from './FileManager/services/AplosFileApiService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import Papa from 'papaparse'
  import { GetAplosFileDataArgs } from './FileManager/models/GetAplosFileDataArgs'
  import { AplosFileContentType } from './FileManager/models/AplosFileContentType'
  import { toast } from 'vue-sonner'

  interface IDataViewerContentProps {
    fileId?: string | null
    getFileDataResponse?: GetFileDataResponse // When loaded with DataExplorer expects csv contentType
  }

  const emit = defineEmits<{
    (e: 'update:getFileDataResponse', value: GetFileDataResponse): void
  }>()

  const props = defineProps<IDataViewerContentProps>()

  const aplosFileApiService: IAplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  const loadingFileData = ref<boolean>(false)
  const hasErrorLoading = ref<boolean>(false)
  const fileData = ref<Papa.ParseResult<unknown>>()
  const tableData = ref<Array<any>>([])
  const columns = ref<Array<any>>([])
  const gridContainer = ref<HTMLElement>()

  // Scroll indicator state
  const canScrollHorizontally = ref(false)
  const canScrollVertically = ref(false)
  const showHorizontalHint = ref(true)
  const showVerticalHint = ref(true)
  const showScrollIndicators = ref(false)

  const revoColumns = computed(() =>
    columns.value.map((col: any) => ({
      prop: col.field,
      name: col.header || col.field,
      size: 140,
      sortable: true,
      filter: true,
      columnProperties: (column: ColumnRegular | ColumnGrouping<any>): CellProps => {
        // Type guard to check it's ColumnRegular
        if ('prop' in column) {
          return {
            style: {
              textTransform: 'none',
              fontWeight: 'bold'
            }
          }
        }
        // Fallback for ColumnGrouping or unexpected input
        return {}
      },
      cellProperties: (): { style?: Record<string, any>; class?: Record<string, boolean> } => {
        return {
          style: {
            borderColor: 'var(--primary)'
          }
        }
      },
      cellCompare: (prop: ColumnProp, a: DataType, b: DataType): number => {
        function parseSortableValue(value: unknown): {
          type: 'number' | 'string'
          val: number | string
        } {
          const num = parseFloat(String(value))
          if (!isNaN(num)) {
            return { type: 'number', val: num }
          }
          return { type: 'string', val: String(value) }
        }

        const aVal = parseSortableValue(a[prop])
        const bVal = parseSortableValue(b[prop])

        // Both numeric
        if (aVal.type === 'number' && bVal.type === 'number') {
          return (aVal.val as number) - (bVal.val as number)
        }

        // Case-insensitive string comparison
        const aStr = String(aVal.val).toLowerCase()
        const bStr = String(bVal.val).toLowerCase()

        if (aStr < bStr) return -1
        if (aStr > bStr) return 1
        return 0
      }
    }))
  )

  const loadFileData = async () => {
    if (!props.fileId && !props.getFileDataResponse) {
      throw Error('Valid input properties required to load data.')
    }
    loadingFileData.value = true
    hasErrorLoading.value = false
    try {
      let csv: string | null = null
      if (props.getFileDataResponse) {
        csv = props.getFileDataResponse.data!
      } else {
        const auth = useUser()
        await auth.initialize()

        const args = new GetAplosFileDataArgs({
          tenantId: auth.user.tenant_id as string,
          userId: auth.user.id as string,
          fileId: props.fileId!,
          contentType: AplosFileContentType.TextCsv
        })

        const response = await aplosFileApiService.getFileData(args)
        csv = response.data.data!
        emit('update:getFileDataResponse', response.data)
      }

      const parseResult = Papa.parse(csv, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true
      })

      if (parseResult.errors && parseResult.errors.length) {
        // handle or throw
        throw new Error(
          `CSV parse failed: ${parseResult.errors.map((e) => e.message || e.code).join('; ')}`
        )
      }

      fileData.value = parseResult
    } catch (error) {
      hasErrorLoading.value = true
      toast.error('Failed to load file data.')
    } finally {
      loadingFileData.value = false
    }
  }

  watch(fileData, (newVal) => {
    const columnNames = newVal?.meta?.fields
    const data = newVal?.data

    if (Array.isArray(columnNames) && Array.isArray(data)) {
      // With Papa.parse({ header: true }) each row is an object keyed by header name
      tableData.value = data as any[]
      columns.value = columnNames.map((col: string) => ({
        field: col,
        header: col
      }))
    } else {
      tableData.value = []
      columns.value = []
    }
  })

  // Scroll detection functions
  const checkScrollability = async () => {
    await nextTick()

    if (!gridContainer.value) {
      return
    }

    // Reset hints visibility
    showHorizontalHint.value = true
    showVerticalHint.value = true

    // For virtualized grids like RevoGrid, check based on data size vs visible area
    const containerRect = gridContainer.value.getBoundingClientRect()
    const rowHeight = 40 // From RevoGrid rowSize prop
    const columnWidth = 140 // From revoColumns computed size

    // Calculate how many rows/columns can fit in the visible area
    const visibleRows = Math.floor(containerRect.height / rowHeight)
    const visibleColumns = Math.floor(containerRect.width / columnWidth)

    // Check if we have more data than can fit
    const hasMoreRows = tableData.value.length > visibleRows
    const hasMoreColumns = columns.value.length > visibleColumns

    // Also try DOM-based detection as fallback
    let domHasHorizontalScroll = false
    let domHasVerticalScroll = false

    const possibleSelectors = [
      '.revogr-viewport-scroll',
      '.revogr-scroll-virtual',
      '.revogr-data',
      '.revogr-overlay-size',
      'revo-grid',
      '[role="grid"]'
    ]

    for (const selector of possibleSelectors) {
      const elements = gridContainer.value.querySelectorAll(selector)
      for (const element of elements) {
        const el = element as HTMLElement
        if (el.scrollWidth > el.clientWidth) domHasHorizontalScroll = true
        if (el.scrollHeight > el.clientHeight) domHasVerticalScroll = true
      }
    }

    // Use data-based detection OR DOM-based detection
    const hasHorizontalScroll = hasMoreColumns || domHasHorizontalScroll
    const hasVerticalScroll = hasMoreRows || domHasVerticalScroll

    canScrollHorizontally.value = hasHorizontalScroll
    canScrollVertically.value = hasVerticalScroll
    showScrollIndicators.value = hasHorizontalScroll || hasVerticalScroll

    // Auto-hide hints after 8 seconds
    if (hasVerticalScroll) {
      setTimeout(() => {
        showVerticalHint.value = false
      }, 4000)
    }
    if (hasHorizontalScroll) {
      setTimeout(() => {
        showHorizontalHint.value = false
      }, 4000)
    }
  }

  // Hide hints on any click inside the component
  const hideHints = () => {
    showHorizontalHint.value = false
    showVerticalHint.value = false
  }

  watch(
    () => [props.fileId, props.getFileDataResponse],
    ([newFileId, newGetFileDataResponse], [oldFileId, oldGetFileDataResponse]) => {
      const idChanged = newFileId && newFileId !== oldFileId
      const responseChanged =
        newGetFileDataResponse && newGetFileDataResponse !== oldGetFileDataResponse

      if (idChanged || responseChanged) {
        loadFileData()
      }
    }
  )

  watch(tableData, () => {
    // Recheck scrollability when data changes
    setTimeout(checkScrollability, 300)
  })

  const errorMessage = ref<string>('An error was encountered loading data.')

  onMounted(() => {
    if (props.fileId || props.getFileDataResponse) loadFileData()
    else {
      if (!props.fileId) errorMessage.value = 'Unable to load data. No file Id supplied.'
      hasErrorLoading.value = true
    }
  })
</script>

<template>
  <div @click="hideHints" class="data-viewer-content">
    <div v-if="hasErrorLoading" class="error-state">{{ errorMessage }}</div>
    <Spinner
      v-else-if="loadingFileData"
      message="Loading file data..."
      :is-loading="loadingFileData"
    />
    <div v-else-if="fileData" class="data-grid-container" ref="gridContainer">
      <!-- Scroll Indicators -->
      <div v-if="showScrollIndicators" class="scroll-indicators">
        <!-- Horizontal scroll indicator -->
        <div
          v-if="canScrollHorizontally"
          class="scroll-hint horizontal"
          :class="{ 'fade-out': !showHorizontalHint }"
        >
          <div class="scroll-hint-content">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M8 3l4 8 5-5v11H5V6l3-3z" />
              <path d="M12 19h10" />
            </svg>
            <span>Scroll horizontally to see more columns</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M16 3l-4 8-5-5v11h14V6l-3-3z" />
              <path d="M12 19H2" />
            </svg>
          </div>
        </div>

        <!-- Vertical scroll indicator -->
        <div
          v-if="canScrollVertically"
          class="scroll-hint vertical"
          :class="{ 'fade-out': !showVerticalHint }"
        >
          <div class="scroll-hint-content">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M7 13l3 3 3-3" />
              <path d="M7 6l3 3 3-3" />
            </svg>
            <span>Scroll down to see more rows</span>
          </div>
        </div>
      </div>

      <RevoGrid
        :columns="revoColumns"
        :source="tableData"
        :rowSize="40"
        :resize="true"
        :range="true"
        :filter="true"
        :theme="'default'"
        :autoSizeColumn="true"
        style="height: 100%; width: 100%"
        class="revo-grid"
      />
    </div>
  </div>
</template>

<style>
  .data-viewer-content {
    height: 100%;
    width: 100%;
  }

  .error-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #ef4444;
    font-weight: 600;
  }

  .data-grid-container {
    height: 100%;
    width: 100%;
    position: relative;
  }

  /* Scroll Indicators */
  .scroll-indicators {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 10;
  }

  .scroll-hint {
    position: absolute;
    background: var(--primary);
    color: var(--foreground);
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease-out;
    transition: opacity 0.5s ease-out;
  }

  .scroll-hint.fade-out {
    opacity: 0;
  }

  .scroll-hint.horizontal {
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
  }

  .scroll-hint.vertical {
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    writing-mode: vertical-rl;
    text-orientation: mixed;
  }

  .scroll-hint-content {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .scroll-hint.vertical .scroll-hint-content {
    flex-direction: column;
    writing-mode: horizontal-tb;
    text-orientation: initial;
  }

  @keyframes slideIn {
    from {
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      transform: translateX(-50%) translateY(0);
    }
  }

  .scroll-hint.vertical {
    animation: slideInVertical 0.3s ease-out;
  }

  @keyframes slideInVertical {
    from {
      transform: translateY(-50%) translateX(10px);
    }
    to {
      transform: translateY(-50%) translateX(0);
    }
  }
</style>
